import { createFileRoute } from "@tanstack/react-router";
import { useSimpleForm } from "@/hooks/use-simple-form";
import useAppStore from "@/store";
import { useTranslate } from "@/hooks/use-translate";
import { Button } from "@/components/ui/button";
import { CreateOrderTest } from "@/components/order/order-test";
import type { Sale } from "@/store/Sale";
import { useEffect, useState } from "react";
import type { Customer } from "@/store/Customer";
import type { PaymentInfo } from "@/store/FinanceAccount";

import { getNetAmount } from "@/store/Order";
import { cn, pick, unPick } from "@/lib/utils";
import { getProfit, getPurchaseTotal, getTotal } from "@/store/Order";
import { ChevronLeft, Plus } from "lucide-react";
import ProcessPayment from "@/components/ui/shared/process-payment";
import SimpleTable from "@/components/ui/table";
import { SalesTableColumns } from "@/components/sale/sale-meta";
import { PriceItem } from "@/components/ui/shared/details";
import AddOrderSale from "@/components/sale/add-order-sale";
import OrderLeftForm from "@/components/order/order-left-form";

export const Route = createFileRoute("/(dasboard)/orders/orders/create")({
  component: CreateOrderPage,
});

function CreateOrderPage() {
  const store = useAppStore();
  const { t, formatDate } = useTranslate();
  const [sales, setSales] = useState<Sale[]>([]);
  const [customer, setCustomer] = useState<Customer | undefined>();

  const form = useSimpleForm({
    status: "pending",
    delivery_status: "pending",
    delivery_fee: 0,
    discount: 0,
    tax: 0,
    shop_id: "",
    customer_id: "",
    user_id: "",
    payments: [],
  });

  useEffect(() => {
    if (store.auth.session) {
      store.financeAccount.fetch();
      store.stockBatch.fetch();
      store.service.fetch();
    }
  }, [store.auth.session]);

  const net_amount = () => {
    return getNetAmount({
      sales,
      ...pick(form.values, ["tax", "delivery_fee", "discount"]),
    });
  };

  const getRemainingAmount = () => {
    return (
      net_amount() -
      form.values.payments
        .map((item: PaymentInfo) => parseInt(item.amount as any))
        .reduce((sum: number, a: number) => sum + a, 0)
    );
  };

  const addPayment = () => {
    form.setValue("payments", [
      ...form.values.payments,
      {
        finance_account_id: "",
        amount: getRemainingAmount(),
        due_date: "",
        payment_date: formatDate(new Date(), "yyyy-MM-dd"),
        amount_given: "",
        change_given: "",
      },
    ]);
  };

  const submit = async () => {
    store.errors.reset();
    const session = store.auth.session;
    // Register the order
    try {
      const data = {
        ...unPick(form.values, ["payments"]),
        shop_id: session?.currentShop.id,
        user_id: session?.user.id,
        sales: sales.map((item) =>
          pick(item, [
            "salable_id",
            "salable_name",
            "selling_price",
            "quantity",
          ])
        ),
      };
      if (form.values.payments.length > 0)
        data["payments"] = form.values.payments.map(
          (item: PaymentInfo) => item
        );

      await store.order.create(data);
      window.history.back();
    } catch (error: any) {
      store.errors.catch(error);
    }
  };

  return (
    <div className="">
      {store.testing.canTest && (
        <CreateOrderTest
          form={form}
          setSales={setSales}
          setCustomer={setCustomer}
        />
      )}

      <div>
        <button
          type="button"
          onClick={() => window.history.back()}
          className="py-2 inline-flex gap-2 items-center justify-center rounded-lg text-sm text-gray-500 hover:text-gray-900 hover:text-gray-900 hover:underline font-semibold"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="">{t("Back")}</span>
        </button>
      </div>

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold md:-mb-4">
          {t("Create an order")}
        </h2>
        <div>
          <Button
            onClick={submit}
            loading={store.order.loading("create")}
            className="py-2"
          >
            {t("Save the order")}
          </Button>
        </div>
      </div>

      <div>{store.errors.render()}</div>

      <section className="flex flex-col lg:flex-row gap-6">
        <aside className="w-full lg:w-1/3 border border-gray-200 p-6 rounded-md bg-white">
          <h3 className="text-lg font-semibold">{t("Order informations")}</h3>

          <OrderLeftForm
            customer={customer}
            setCustomer={setCustomer}
            form={form}
            sales={sales}
            addPayment={addPayment}
          />

          {form.values.status == "approved" && (
            <>
              <ProcessPayment
                form={form}
                allowCashDetails={true}
                accountClass="col-span-2"
                net_amount={net_amount()}
              />
              <Button
                onClick={addPayment}
                className="mt-2 border-none bg-transparent text-primary-500 font-semibold hover:underline hover:bg-transparent px-0"
              >
                <Plus className="w-4 h-4" />
                {t("Add Payment")}
              </Button>
            </>
          )}
        </aside>

        <aside className="w-full lg:w-2/3 border border-gray-200 bg-white rounded-md p-6">
          <h3 className="text-lg font-semibold">{t("Order sale items")}</h3>
          <div className="mt-4">
            <AddOrderSale setSales={setSales} />
          </div>

          <div className="mt-4">
            <SimpleTable
              items={sales}
              rowClass="hover:bg-white"
              cellClass="px-2"
              containerClass="shadow-none border border-gray-200"
              columns={SalesTableColumns({
                store,
                updateItem: (updates: Partial<Sale>) => {
                  setSales((items: Sale[]) => {
                    return items.map((item) => {
                      if (item.id == updates.id) {
                        return {
                          ...item,
                          ...updates,
                        };
                      } else {
                        return item;
                      }
                    });
                  });
                },
                onDelete: (sale: Sale) => {
                  setSales((items: Sale[]) => {
                    return items.filter((item) => item.id != sale.id);
                  });
                },
              })}
            />
            <div className="bg-gray-100 mt-2 rounded-md p-4 flex w-full">
              <div className="w-full">
                <PriceItem label={t("Total amount")} value={getTotal(sales)} />

                <div className="border-b border-gray-300 border-dashed my-1"></div>
                <PriceItem
                  label={t("Total purchasing price")}
                  value={getPurchaseTotal(sales)}
                  color="text-red-800"
                />
                <PriceItem
                  label={t("Delivery fee")}
                  value={form.values.delivery_fee}
                  color="text-green-800"
                />

                <PriceItem
                  label={t("Discount")}
                  value={form.values.discount}
                  color="text-red-800"
                />

                <PriceItem
                  label={t("Tax")}
                  value={form.values.tax}
                  surfix=" %"
                  color="text-green-800"
                />

                <PriceItem
                  label={t("Profit")}
                  value={getProfit({
                    sales,
                    ...pick(form.values, ["tax", "discount", "delivery_fee"]),
                  })}
                  color={cn("text-green-800", {
                    "text-red-800":
                      getProfit({
                        sales,
                        ...pick(form.values, [
                          "tax",
                          "discount",
                          "delivery_fee",
                        ]),
                      }) < 0,
                  })}
                />
                <div className="border-b border-gray-300 border-dashed my-1"></div>
                <PriceItem
                  label={t("Net Amount")}
                  value={getNetAmount({
                    sales,
                    ...pick(form.values, ["tax", "delivery_fee", "discount"]),
                  })}
                />
              </div>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
